<template>
  <MtxPage width="md">
    <template #header>
      <h1>Edit Tournament</h1>
    </template>

    <MtxCard>
      <MtxLoading v-if="isLoading" />

      <form v-else-if="tournament" @submit.prevent>
        <MtxInput
          v-model="localTournament.title"
          :validation="$v.localTournament.title"
          label="Title"
          class="mb-2"
          required
        />

        <MtxInput
          v-model="localTournament.description"
          :validation="$v.localTournament.description"
          label="Description"
          type="textarea"
          class="mb-2"
          required
        />

        <MtxUpload
          v-model="localTournament.ipfsFiles"
          @loading="isUploading = $event"
          label="IPFS Hash"
          class="mb-2"
        />

        <!-- <MtxInput
            v-model="localTournament.url"
            label="URL"
            type="url"
            class="mb-2"
          />-->

        <MtxInput
          v-model="localTournament.category"
          label="Category"
          type="select"
          :options="categories"
          class="mb-2"
        />

        <MtxInput
          :value="localTournament.entryFee"
          :min="$v.localTournament.entryFee.$params.min.min"
          :validation="$v.localTournament.entryFee"
          @input="localTournament.entryFee = +$event"
          label="Entry Fee"
          type="number"
          step="any"
          class="mb-2"
        />

        <div class="text-center">
          <button
            @click="requireWeb3(onSubmit)"
            :class="{ 'btn--disabled': disabled }"
            type="submit"
          >
            Submit
          </button>
        </div>
      </form>
    </MtxCard>
  </MtxPage>
</template>

<script>
import isEqual from "lodash.isequal"
import { mapState } from "vuex"
import { required, maxLength, minValue, decimal } from "@/utils/validators"
import MatryxTournament from "@/web3/contracts/MatryxTournament"
import exitGuard from "@/mixins/exitGuard"
import nprogress from "@/mixins/nprogress"
import web3 from "@/mixins/web3"
import API from "@/api"

export default {
  mixins: [exitGuard, nprogress, web3],

  props: {
    tournamentAddress: {
      type: String,
      required: true
    }
  },

  data: () => ({
    isUploading: false,
    isLoading: false,
    categories: [
      { value: "", label: "Select a category", disabled: true },
      { value: "art", label: "Art" },
      { value: "environment", label: "Environment" },
      { value: "health", label: "Health" },
      { value: "math", label: "Math" },
      { value: "science", label: "Science" },
      { value: "technology", label: "Technology" }
    ],
    localTournament: {},
    tournament: {}
  }),

  metaInfo: {
    titleTemplate: `%s | Edit tournament`
  },

  computed: {
    ...mapState({
      tournamentAbi: state => state.contracts.artifacts.tournament.abi
    }),

    hasChanges() {
      return !isEqual(this.localTournament, this.tournament)
    },

    disabled() {
      return this.isUploading || this.isLoading || this.$v.$invalid || !this.hasChanges
    },

    preventExit() {
      return this.hasChanges
    }
  },

  created() {
    this.fetchTournament()
  },

  methods: {
    async fetchTournament() {
      this.isLoading = true

      const { success, data, status } = await API.getTournament(this.tournamentAddress)
      if (!success) {
        if (status === 400) {
          return this.$router.push({ name: "Error400" })
        }
        if (status === 404) {
          return this.$router.replace({ name: "Error404", params: { resource: "tournament" } })
        }
        return this.$router.push({ name: "Error500" })
      }

      this.tournament = data.tournament
      this.localTournament = { ...data.tournament }
      this.isLoading = false
    },

    async onSubmit() {
      this.$v.$touch()
      if (this.disabled) return

      try {
        const upload = API.uploadJson({
          title: this.localTournament.title,
          description: this.localTournament.description,
          category: this.localTournament.category,
          ipfsFiles: this.localTournament.ipfsFiles
        })

        this.$web3.$emit("upload", upload)
        const { data } = await upload

        const params = {
          content: data.hash,
          entryFee: this.localTournament.entryFee || 0
        }
        const Tournament = new MatryxTournament(this.tournament.address, this.tournamentAbi)

        const tx = this.addPlannedTx({
          txFn: () => Tournament.updateDetails(params),
          header: "Edit Tournament"
        })

        await tx
        this.tournament = { ...this.localTournament }

        this.$router.push({
          name: "TournamentsSingle",
          params: { address: this.tournamentAddress }
        })
      } catch (error) {
        if (error.message.includes("User denied transaction signature")) return

        this.$notify({
          title: "Error",
          type: "error",
          text: "There was an error editing the tournament."
        })
      }
    }
  },

  validations() {
    return {
      localTournament: {
        title: {
          required,
          maxLength: maxLength(255)
        },
        description: {
          required,
          maxLength: maxLength(2000)
        },
        entryFee: {
          required,
          min: minValue(0),
          decimal
        }
      }
    }
  }
}
</script>
